import { FC, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { ReportShell } from '../components/ReportShell'

interface SellerReportLinkItem {
  category: 'Performance' | 'Mix de Canal' | 'Evolução e Tendência' | 'Ação Comercial'
  title: string
  description: string
  path: string
}

const reportLinks: SellerReportLinkItem[] = [
  {
    category: 'Performance',
    title: 'Ticket Médio por Vendedor',
    description: 'Ticket médio por vendedor e consolidado geral no período selecionado.',
    path: '/seller/reports/ticket-average',
  },
  {
    category: 'Performance',
    title: 'Faturamento por Vendedor',
    description: 'Ranking de faturamento e participação por vendedor.',
    path: '/seller/reports/revenue',
  },
  {
    category: 'Performance',
    title: 'Volume de Pedidos',
    description: 'Quantidade de pedidos por vendedor e distribuição percentual.',
    path: '/seller/reports/order-volume',
  },
  {
    category: 'Mix de Canal',
    title: 'Comparativo por Origem',
    description: 'Comparação entre Ninegrid e TOTVS por pedidos e receita.',
    path: '/seller/reports/source-comparison',
  },
  {
    category: 'Evolução e Tendência',
    title: 'Evolução por Período',
    description: 'Evolução temporal de pedidos, receita e ticket médio.',
    path: '/seller/reports/period-evolution',
  },
  {
    category: 'Evolução e Tendência',
    title: 'Tendência de Ticket Médio',
    description: 'Variação do ticket médio por dia, semana ou mês.',
    path: '/seller/reports/ticket-trend',
  },
  {
    category: 'Evolução e Tendência',
    title: 'Tendência por Vendedor',
    description: 'Comparativo de ticket médio por vendedor versus período anterior.',
    path: '/seller/reports/ticket-trend-seller',
  },
  {
    category: 'Performance',
    title: 'Concentração por Vendedor',
    description: 'Curva ABC de vendedores com participação acumulada de receita.',
    path: '/seller/reports/seller-concentration',
  },
  {
    category: 'Mix de Canal',
    title: 'Mix por Origem e Vendedor',
    description: 'Cruza vendedor x origem para identificar dependência de canal.',
    path: '/seller/reports/source-mix-seller',
  },
  {
    category: 'Performance',
    title: 'Faixas de Ticket',
    description: 'Distribuição de pedidos e receita por faixas de valor.',
    path: '/seller/reports/ticket-bands',
  },
  {
    category: 'Mix de Canal',
    title: 'Dependência de Canal',
    description: 'Classifica nível de dependência de Ninegrid ou TOTVS por vendedor.',
    path: '/seller/reports/channel-dependency',
  },
  {
    category: 'Ação Comercial',
    title: 'Oportunidade de Migração',
    description: 'Separa perfis para expansão, recuperação de canal e monitoramento.',
    path: '/seller/reports/migration-opportunity',
  },
  {
    category: 'Mix de Canal',
    title: 'Cobertura por Canal',
    description: 'Mostra sub-representação de canais por faixa de ticket e por vendedor.',
    path: '/seller/reports/channel-coverage',
  },
  {
    category: 'Performance',
    title: 'Taxa de Conversão por Atendimento',
    description: 'Conversão por vendedor baseada em sessões da fila (conservadora e potencial).',
    path: '/seller/reports/conversion-rate',
  },
  {
    category: 'Performance',
    title: 'SLA de Atendimento por Vendedor',
    description: 'Tempo de espera e atendimento por vendedor com taxa de cumprimento de SLA.',
    path: '/seller/reports/attendance-sla',
  },
  {
    category: 'Ação Comercial',
    title: 'Motivos de Não Venda por Vendedor',
    description: 'Cruza vendedores e motivos de perda para direcionar treinamento e correção de processo.',
    path: '/seller/reports/no-sale-reasons-seller',
  },
  {
    category: 'Ação Comercial',
    title: 'Tempo de permanência em checkout pendente',
    description: 'Mostra há quanto tempo os pending checkout permanecem abertos, por faixa e por vendedor.',
    path: '/seller/reports/pending-checkout-aging',
  },
  {
    category: 'Ação Comercial',
    title: 'Comparação Conversão x Tempo de Permanência (Aging)',
    description: 'Cruza conversão com backlog de pending checkout para priorizar follow-up comercial por vendedor.',
    path: '/seller/reports/conversion-aging-correlation',
  },
  {
    category: 'Ação Comercial',
    title: 'Follow-up Backlog por Vendedor',
    description: 'Fila operacional de pending checkout priorizada por ação (hoje, 24h+, 48h+).',
    path: '/seller/reports/pending-checkout-followup',
  },
  {
    category: 'Ação Comercial',
    title: 'Painel Executivo Operacional',
    description: 'Consolida conversão, aging e backlog crítico em uma única visão para decisão rápida.',
    path: '/seller/reports/operational-executive',
  },
]

const categoryOrder: Array<SellerReportLinkItem['category']> = [
  'Performance',
  'Evolução e Tendência',
  'Mix de Canal',
  'Ação Comercial',
]

const categoryDescriptions: Record<SellerReportLinkItem['category'], string> = {
  Performance: 'Indicadores de resultado por vendedor e por faixa de ticket.',
  'Evolução e Tendência': 'Análises de comportamento no tempo e variação entre períodos.',
  'Mix de Canal': 'Comparativos Ninegrid x TOTVS e pontos de sub-representação de canal.',
  'Ação Comercial': 'Relatórios priorizados para direcionar plano de ação comercial.',
}

const SellerReportsIndexPage: FC = () => {
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] =
    useState<SellerReportLinkItem['category'] | 'Todas'>('Todas')

  const normalizedSearch = search.trim().toLowerCase()

  const filteredReports = useMemo(() => {
    return reportLinks.filter((report) => {
      const categoryMatch = selectedCategory === 'Todas' || report.category === selectedCategory
      if (!categoryMatch) {
        return false
      }

      if (!normalizedSearch) {
        return true
      }

      const haystack = `${report.title} ${report.description} ${report.category}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [normalizedSearch, selectedCategory])

  const reportsByCategory = categoryOrder
    .map((category) => ({
      category,
      items: filteredReports.filter((report) => report.category === category),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Relatorios do Vendedor', path: '/seller/reports', isActive: true }]}>
        Hub de Relatórios do Vendedor
      </PageTitle>

      <ReportShell
        title='Índice Central de Relatórios'
        description='Ponto único de navegação para todos os relatórios do módulo Vendedor, com acesso rápido por cards.'
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-8'>
              <label className='form-label fw-semibold'>Buscar relatório</label>
              <input
                className='form-control'
                type='text'
                value={search}
                placeholder='Ex: ticket, cobertura, migração...'
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Categoria</label>
              <select
                className='form-select'
                value={selectedCategory}
                onChange={(event) =>
                  setSelectedCategory(event.target.value as SellerReportLinkItem['category'] | 'Todas')
                }
              >
                <option value='Todas'>Todas</option>
                {categoryOrder.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        }
      >
        {reportsByCategory.length > 0 ? (
          <div className='d-flex flex-column gap-6'>
            {reportsByCategory.map(({ category, items }) => (
              <div key={category}>
                <div className='mb-4'>
                  <div className='fs-3 fw-bold text-dark'>{category}</div>
                  <div className='text-gray-600 mt-1'>{categoryDescriptions[category]}</div>
                </div>

                <div className='row g-4'>
                  {items.map((report) => (
                    <div className='col-12 col-md-6 col-xl-4' key={report.path}>
                      <Link to={report.path} className='card border-0 shadow-sm h-100 text-decoration-none'>
                        <div className='card-body d-flex flex-column'>
                          <div className='text-gray-900 fw-bold fs-4 mb-2'>{report.title}</div>
                          <div className='text-gray-600 fs-6 flex-grow-1'>{report.description}</div>
                          <div className='mt-4 text-primary fw-semibold'>Abrir relatório</div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='alert alert-light-info mb-0'>
            Nenhum relatório encontrado com os filtros informados.
          </div>
        )}
      </ReportShell>
    </>
  )
}

export { SellerReportsIndexPage }